import {
    Component,
    Input,
    Output,
    EventEmitter,
    forwardRef,
    OnInit,
    OnDestroy,
    signal,
    computed,
    effect,
} from '@angular/core'
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    FormControl,
    ReactiveFormsModule,
    Validators,
    ValidatorFn,
} from '@angular/forms'
import { ZardTooltipDirective } from '@/styles/components/tooltip'
import {
    ZardFormFieldComponent,
    ZardFormControlComponent,
    ZardFormLabelComponent,
    ZardFormMessageComponent,
} from '@/styles/components/form'
import { ZardSelectComponent, ZardSelectItemComponent } from '@/styles/components/select'
import { CommonModule } from '@angular/common'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'
import { Subject, takeUntil } from 'rxjs'
import {
    TFormFieldAppearance,
    IDropdownOption,
} from '@app/core/models/configurable-components.model'
import { SHIKSHA_FUNDER_DETAIL_CONSTANTS } from '@app/features/crm/constants/shiksha-funder-detail.constants'
import {
    CONTACT_PLACEHOLDER_NOT_AVAILABLE,
    isContactDisplayEmptyValueToken,
} from '@app/core/constants/client.constants'

@Component({
    selector: 'app-configurable-dropdown',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ZardFormFieldComponent,
        ZardFormControlComponent,
        ZardFormLabelComponent,
        ZardFormMessageComponent,
        ZardSelectComponent,
        ZardSelectItemComponent,
        ZardTooltipDirective,
        SvgIconComponent,
    ],
    templateUrl: './configurable-dropdown.component.html',
    styleUrls: ['./configurable-dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ConfigurableDropdownComponent),
            multi: true,
        },
    ],
})
export class ConfigurableDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() set label(value: string | undefined) {
        this._label.set(value)
    }
    @Input() set placeholder(value: string | undefined) {
        this._placeholder.set(value)
    }
    @Input() set hint(value: string | undefined) {
        this._hint.set(value)
    }
    @Input() set appearance(value: TFormFieldAppearance) {
        this._appearance.set(value)
    }
    @Input() set disabled(value: boolean) {
        this._disabled.set(value)
    }
    @Input() set multiple(value: boolean) {
        this._multiple.set(value)
    }
    @Input() set showEmptyOption(value: boolean) {
        this._showEmptyOption.set(value)
    }
    @Input() set emptyOptionText(value: string) {
        this._emptyOptionText.set(value)
    }
    @Input() set outlineColor(value: string | undefined) {
        this._outlineColor.set(value)
    }
    @Input() set textColor(value: string | undefined) {
        this._textColor.set(value)
    }
    @Input() set placeholderColor(value: string | undefined) {
        this._placeholderColor.set(value)
    }
    @Input() set width(value: string | undefined) {
        this._width.set(value)
    }
    @Input() set height(value: string | undefined) {
        this._height.set(value)
    }
    @Input() set options(value: IDropdownOption[]) {
        this._options.set(value)
    }
    @Input() set required(value: boolean) {
        this._required.set(value)
    }
    @Input() set customValidators(value: ValidatorFn[] | undefined) {
        this._customValidators.set(value)
    }
    @Input() set panelClass(value: string | undefined) {
        this._panelClass.set(value)
    }
    @Input() set panelMaxHeight(value: number | undefined) {
        this._panelMaxHeight.set(value)
    }
    /** When set, used as the error message for required validation instead of "{label} is required". */
    @Input() requiredErrorMessage: string | undefined
    /** When true, do not render the error message (e.g. when parent shows it). */
    @Input() hideErrorMessage = false
    /** When set, show this message as the error below the control (same style as internal error). */
    @Input() errorMessage: string | undefined
    @Input() set zSelectAppearance(value: 'default' | 'filter' | undefined) {
        this._zSelectAppearance.set(value ?? 'default')
    }

    @Output() openedChange = new EventEmitter<boolean>()
    @Output() selectionChange = new EventEmitter<{
        value: string | number | (string | number)[] | null
    }>()

    private readonly _label = signal<string | undefined>(undefined)
    private readonly _placeholder = signal<string | undefined>(undefined)
    private readonly _hint = signal<string | undefined>(undefined)
    private readonly _appearance = signal<TFormFieldAppearance>('outline')
    private readonly _disabled = signal<boolean>(false)
    private readonly _multiple = signal<boolean>(false)
    private readonly _showEmptyOption = signal<boolean>(false)
    private readonly _emptyOptionText = signal<string>('-- Select --')
    private readonly _outlineColor = signal<string | undefined>(undefined)
    private readonly _textColor = signal<string | undefined>(undefined)
    private readonly _placeholderColor = signal<string | undefined>(undefined)
    private readonly _width = signal<string | undefined>(undefined)
    private readonly _height = signal<string | undefined>(undefined)
    private readonly _options = signal<IDropdownOption[]>([])
    private readonly _required = signal<boolean>(false)
    private readonly _customValidators = signal<ValidatorFn[] | undefined>(undefined)
    private readonly _panelClass = signal<string | undefined>(undefined)
    private readonly _panelMaxHeight = signal<number | undefined>(undefined)
    private readonly _zSelectAppearance = signal<'default' | 'filter'>('default')
    private readonly _zSelectValue = signal<string | string[]>('')

    readonly labelSignal = this._label.asReadonly()
    readonly placeholderSignal = this._placeholder.asReadonly()
    readonly hintSignal = this._hint.asReadonly()
    readonly appearanceSignal = this._appearance.asReadonly()
    readonly disabledSignal = this._disabled.asReadonly()
    readonly multipleSignal = this._multiple.asReadonly()
    readonly showEmptyOptionSignal = this._showEmptyOption.asReadonly()
    readonly emptyOptionTextSignal = this._emptyOptionText.asReadonly()
    readonly outlineColorSignal = this._outlineColor.asReadonly()
    readonly textColorSignal = this._textColor.asReadonly()
    readonly placeholderColorSignal = this._placeholderColor.asReadonly()
    readonly widthSignal = this._width.asReadonly()
    readonly heightSignal = this._height.asReadonly()
    readonly optionsSignal = this._options.asReadonly()
    readonly requiredSignal = this._required.asReadonly()
    readonly customValidatorsSignal = this._customValidators.asReadonly()
    readonly panelClassSignal = this._panelClass.asReadonly()
    readonly panelMaxHeightSignal = this._panelMaxHeight.asReadonly()
    readonly zSelectAppearanceSignal = this._zSelectAppearance.asReadonly()
    readonly zSelectValue = this._zSelectValue.asReadonly()

    private readonly lastResolvedLabel = signal<string>('')

    private readonly resolvedLabel = computed(() => {
        const value = this._zSelectValue()
        if (value == null || (Array.isArray(value) && value.length === 0)) return ''

        if (this.multipleSignal() && Array.isArray(value)) {
            const selectedOptions = value
                .map((v) =>
                    this.optionsSignal().find(
                        (opt) => String(opt.value).trim() === String(v).trim(),
                    ),
                )
                .filter((opt): opt is IDropdownOption => !!opt)
            if (selectedOptions.length === 0) return ''
            const first = selectedOptions[0]
            if (this.isContactOption(first)) {
                let label = this.getContactName(first)
                const designation = this.getContactDesignation(first)
                if (designation) label += ` (${designation})`
                if (selectedOptions.length > 1) {
                    label += ` +${selectedOptions.length - 1}`
                }
                return label
            }
            return selectedOptions.map((o) => o.label).join(', ')
        }

        const needle = String(value).trim()
        const selectedOption = this.optionsSignal().find(
            (opt) => String(opt.value).trim() === needle,
        )
        if (!selectedOption) return ''

        // Contact options: show "Name (Role)".
        if (this.isContactOption(selectedOption)) {
            let label = this.getContactName(selectedOption)
            const designation = this.getContactDesignation(selectedOption)
            if (designation) label += ` (${designation})`
            return label
        }

        // Regular options: show their label (including "Select Designation", etc.)
        return selectedOption.label ?? selectedOption.label
    })

    /** Multi-select: full list for native title tooltip (one line per selection). */
    readonly multiSelectContactTooltip = computed(() => {
        if (!this.multipleSignal()) {
            return ''
        }
        const value = this._zSelectValue()
        if (!Array.isArray(value) || value.length < 2) {
            return ''
        }
        const selectedOptions = value
            .map((val) => this.optionsSignal().find((opt) => String(opt.value) === val))
            .filter((opt): opt is IDropdownOption => !!opt)
        if (selectedOptions.length < 2) {
            return ''
        }
        return selectedOptions
            .map((opt) =>
                this.isContactOption(opt)
                    ? this.getContactTriggerLabel(opt)
                    : (opt.label ?? String(opt.value)),
            )
            .join('\n')
    })

    /** Value → label for z-select trigger when projected items are not matched yet (e.g. Call Status id). */
    readonly valueLabelMapForZSelect = computed<Readonly<Record<string, string>> | null>(() => {
        if (this.multipleSignal()) return null
        const opts = this.optionsSignal()
        if (!opts.length) return null
        const out: Record<string, string> = {}
        for (const o of opts) {
            const k = String(o.value).trim()
            if (k !== '') out[k] = String(o.label ?? '')
        }
        return Object.keys(out).length ? out : null
    })

    readonly displayLabel = computed(() => {
        const zValue = this._zSelectValue()
        const current = this.resolvedLabel()
        if (current) return current

        // When options are re-rendered async (or momentarily mismatch),
        // keep showing the last resolved label instead of appearing "reset".
        return zValue ? this.lastResolvedLabel() : ''
    })

    constructor() {
        // Keep `lastResolvedLabel` in sync with resolved label + current value.
        effect(() => {
            const zValue = this._zSelectValue()
            const current = this.resolvedLabel()
            if (!zValue) {
                this.lastResolvedLabel.set('')
                return
            }
            if (current) this.lastResolvedLabel.set(current)
        })
    }

    readonly control = new FormControl<string | number | (string | number)[] | null>('')

    private readonly destroy$ = new Subject<void>()
    private onChange = (value: string | number | (string | number)[] | null) => {}
    private onTouched = () => {}

    ngOnInit(): void {
        this.setupControl()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    private setupControl(): void {
        const validators: ValidatorFn[] = []

        if (this.requiredSignal()) {
            validators.push(Validators.required)
        }

        if (this.customValidatorsSignal()) {
            validators.push(...this.customValidatorsSignal()!)
        }

        this.control.setValidators(validators)
    }

    private toZSelectValue(value: string | number | (string | number)[] | null): string | string[] {
        if (this.multipleSignal() && Array.isArray(value)) {
            return value.map((v) => String(v))
        }
        return value != null ? String(value) : ''
    }

    private findOriginalValue(stringValue: string): string | number {
        const option = this.optionsSignal().find((opt) => String(opt.value) === stringValue)
        return option ? option.value : stringValue
    }

    onZSelectChange(selectedValue: string | string[]): void {
        if (this.multipleSignal() && Array.isArray(selectedValue)) {
            const originalValues = selectedValue.map((sv) => this.findOriginalValue(sv))
            this.control.setValue(originalValues, { emitEvent: false })
            this._zSelectValue.set(selectedValue)
            this.onChange(originalValues)
            this.selectionChange.emit({ value: originalValues })
        } else {
            const sv = selectedValue as string
            if (sv === '' || sv === null) {
                this.control.setValue('', { emitEvent: false })
                this._zSelectValue.set('')
                this.onChange('')
                this.selectionChange.emit({ value: '' })
            } else {
                const originalValue = this.findOriginalValue(sv)
                this.control.setValue(originalValue, { emitEvent: false })
                this._zSelectValue.set(sv)
                this.onChange(originalValue)
                this.selectionChange.emit({ value: originalValue })
            }
        }
        this.onTouched()
    }

    getErrorMessage(): string {
        if (this.control.hasError('required')) {
            const custom = this.requiredErrorMessage?.trim()
            return custom || `${this.labelSignal() || 'This field'} is required`
        }

        return 'Invalid selection'
    }

    writeValue(value: string | number | (string | number)[] | null): void {
        this.control.setValue(value, { emitEvent: false })
        this._zSelectValue.set(this.toZSelectValue(value))
    }

    registerOnChange(fn: (value: string | number | (string | number)[] | null) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.control.disable()
        } else {
            this.control.enable()
        }
    }

    isContactOption(option: IDropdownOption): boolean {
        if (!option.data || typeof option.data !== 'object' || option.data === null) {
            return false
        }
        return 'name' in option.data
    }

    getContactName(option: IDropdownOption): string {
        const contact = option.data as { name?: string } | null
        return contact?.name?.trim() || '-'
    }

    /** "Name (Role)" for trigger display when contact option */
    getContactTriggerLabel(option: IDropdownOption): string {
        if (!option.data || !this.isContactOption(option)) return ''
        const name = this.getContactName(option)
        const designation = this.getContactDesignation(option)
        return designation ? `${name} (${designation})` : name
    }

    getContactDesignation(option: IDropdownOption): string | null {
        const contact = option.data as { title?: string } | null
        return contact?.title?.trim() || null
    }

    getContactEmail(option: IDropdownOption): string | null {
        const contact = option.data as { email?: string } | null
        const email = contact?.email?.trim() || ''
        if (!email || isContactDisplayEmptyValueToken(email.toLowerCase())) {
            return null
        }
        return email
    }

    getContactPhone(option: IDropdownOption): string | null {
        const contact = option.data as { phone?: string } | null
        const phone = contact?.phone?.trim() || ''
        if (!phone || isContactDisplayEmptyValueToken(phone.toLowerCase())) {
            return null
        }
        return phone
    }

    /** Email for contact option details row; missing values show as NA. */
    getContactEmailDisplay(option: IDropdownOption): string {
        return this.getContactEmail(option) || CONTACT_PLACEHOLDER_NOT_AVAILABLE
    }

    /** Phone for contact option details row; missing values show as NA. */
    getContactPhoneDisplay(option: IDropdownOption): string {
        return this.getContactPhone(option) || CONTACT_PLACEHOLDER_NOT_AVAILABLE
    }

    getContactAvatar(option: IDropdownOption): string | null {
        const contact = option.data as { avatar?: string | null } | null
        const url = contact?.avatar?.trim()
        return url || null
    }

    isContactPoc(option: IDropdownOption): boolean {
        const contact = option.data as { isPoc?: boolean } | null
        return contact?.isPoc === true
    }

    getPocBadgeLabel(): string {
        return SHIKSHA_FUNDER_DETAIL_CONSTANTS.UI.CONTACT_BOOK.LABELS.CONTACT_PRIMARY_BADGE
    }

    getPocBadgeTooltip(): string {
        return SHIKSHA_FUNDER_DETAIL_CONSTANTS.UI.CONTACT_BOOK.LABELS.CONTACT_PRIMARY_BADGE_TOOLTIP
    }

    getSelectedContactOption(): IDropdownOption | null {
        const selectedValue = this.control.value
        if (selectedValue === null || selectedValue === undefined) {
            return null
        }
        if (this.multipleSignal() && Array.isArray(selectedValue)) {
            return selectedValue?.length
                ? this.optionsSignal().find((option) => option.value === selectedValue[0]) || null
                : null
        }
        return this.optionsSignal().find((option) => option.value === selectedValue) || null
    }

    getSelectedContactOptions(): IDropdownOption[] {
        const selectedValue = this.control.value
        if (!this.multipleSignal() || !Array.isArray(selectedValue) || selectedValue.length === 0) {
            return []
        }
        return this.optionsSignal().filter((option) => selectedValue.includes(option.value))
    }

    getSelectedContactsCount(): number {
        const selectedValue = this.control.value
        if (!this.multipleSignal() || !Array.isArray(selectedValue)) {
            return 0
        }
        return selectedValue.length
    }

    hasContactOptionsSelected(): boolean {
        const selectedOptions = this.getSelectedContactOptions()
        return (
            selectedOptions.length > 0 &&
            selectedOptions.some((option) => this.isContactOption(option))
        )
    }

    getSelectedNonContactOptions(): IDropdownOption[] {
        const selectedOptions = this.getSelectedContactOptions()
        return selectedOptions.filter((option) => !this.isContactOption(option))
    }

    getSelectedLabelsAsCommaSeparated(): string {
        const selectedOptions = this.getSelectedContactOptions()
        if (!selectedOptions?.length) {
            return ''
        }
        return selectedOptions.map((option) => option.label).join(', ')
    }

    getSelectedContactNamesAsCommaSeparated(): string {
        const selectedOptions = this.getSelectedContactOptions()
        if (!selectedOptions?.length) {
            return ''
        }
        return selectedOptions
            .map((option) => {
                if (this.isContactOption(option)) {
                    return this.getContactName(option)
                }
                return option.label
            })
            .join(', ')
    }
}
